<template>
  <div
    class="results-container"
    :class="
      selectionMode
        ? ['selection-mode'].concat(multipleSelectionMode ? ['multiple'] : ['single'])
        : []
    "
  >
    <div
      v-if="visibleRecipes.length === 0"
      class="has-text-centered is-size-5 is-italic"
      :class="!selectionMode ? 'mt-6' : ''"
    >
      <p>No recipes matched your search criteria.</p>
      <p><a @click.prevent="handleClearSearch">Clear search</a></p>
    </div>

    <!-- `recipe-list` is used by tests, so don't delete it even if no styles are defined for it. -->
    <table v-else class="table is-fullwidth is-striped recipe-list">
      <thead>
        <tr>
          <th
            v-if="selectionMode"
            class="col-checkbox no-print"
            :class="{ 'highlight-checkbox-pulse': showingSelectionPrompt }"
          >
            <a
              v-if="multipleSelectionMode"
              href="#"
              class="plus-button"
              title="Add results to selection"
              @click.prevent="handleSelectAll(true)"
            >
              <span class="icon">
                <i class="far fa-plus-square"></i>
              </span>
            </a>
            <a
              v-if="multipleSelectionMode"
              href="#"
              class="minus-button is-hidden-touch"
              title="Remove results from selection"
              @click.prevent="handleSelectAll(false)"
            >
              <span class="icon">
                <i class="far fa-minus-square"></i>
              </span>
            </a>
          </th>

          <!-- 'name' rather than 'col-name': tests depend on that selector. Don't change it unless you update tests. -->
          <th class="name" width="1">
            <a href="#" @click.prevent="handleSortClick('name')">Name</a>
          </th>

          <th v-if="showTags" class="is-hidden-touch is-hidden-desktop-only">Tags</th>

          <th
            v-if="showModified"
            class="col-modified"
            :class="alwaysShowModifiedColumn ? '' : 'is-hidden-touch is-hidden-desktop-only'"
          >
            <a href="#" @click.prevent="handleSortClick('updated_at')"> Modified </a>
          </th>

          <th v-if="showRecipeActionsColumn" class="no-print"></th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="(recipe, index) in visibleRecipes"
          :id="`recipe-row-${recipe.id}`"
          :key="recipe.id"
          :class="[
            { 'in-selection': selection.includes(recipe.id) },
            { 'is-expanded': expanded[index] },
          ]"
        >
          <td v-if="selectionMode" class="col-checkbox no-print">
            <label>
              <input
                type="checkbox"
                class="checkbox"
                :class="{ 'highlight-checkbox-pulse': showingSelectionPrompt }"
                :checked="selection.includes(recipe.id)"
                @change="handleSelectOne($event.target.checked, recipe)"
              />
            </label>
          </td>

          <!-- 'name' rather than 'col-name': tests depend on that selector. Don't change it unless you update tests. -->
          <td class="name">
            <RecipeNameCell
              link
              :recipe="recipe"
              :show-lock-status="showRecipeActionsColumn"
              :allow-locking="allowLocking"
              :target-new-tab="shouldOpenInNewTab(recipe)"
            />
            <span class="is-italic updated-at-v is-size-7">
              updated {{ dateDisplay(recipe.updated_at) }}
            </span>
          </td>

          <td v-if="showTags" class="col-tags">
            <span class="tag-list-v">
              <RecipeTagInput
                v-if="allowInlineTagEdits && !recipe.locked"
                :user-id="String(recipe.user_id)"
                :recipe-id="String(recipe.id)"
                :tags="recipe.tags"
                :can-click="true"
                empty-caption=""
                @tag-click="(tag) => handleTagClick(tag)"
              />
              <TagLabel
                v-for="tag in sortTags(recipe.tags)"
                v-else
                :key="tag.id"
                :data-test-id="`tag-${tag.name}`"
                :tag-id="tag.id"
                :color="tag.color"
                :name="tag.name"
                :can-click="true"
                :can-remove="false"
                size="small"
                @tag-click="(tag) => handleTagClick(tag)"
              />
            </span>
            <span
              v-if="showModified && !alwaysShowModifiedColumn"
              class="is-hidden-mobile is-hidden-widescreen is-italic is-size-7"
            >
              updated {{ dateDisplay(recipe.updated_at) }}
            </span>
          </td>

          <td
            v-if="showModified"
            class="col-modified"
            :class="alwaysShowModifiedColumn ? '' : 'is-hidden-touch is-hidden-desktop-only'"
          >
            {{ dateDisplay(recipe.updated_at) }}
          </td>

          <td v-if="showRecipeActionsColumn" class="col-actions no-print">
            <div class="is-flex gap-2 is-justify-content-flex-end">
              <RecipePagesButton class="is-hidden-mobile" :recipe="recipe" />
              <RecipeActionsButton
                data-test-id="recipe-actions"
                :recipe="recipe"
                :enable-archiving="enableArchiving"
                :allow-locking="allowLocking"
                :delete-confirms="deleteConfirms"
              />
            </div>

            <a
              class="expand-button-v is-hidden-tablet ml-2"
              data-expand-row
              @click.prevent="() => (expanded[index] = !expanded[index])"
            >
              <span class="icon">
                <i class="fa fa-fw fa-chevron-right"></i>
              </span>
            </a>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>import { ref as _ref } from 'vue';

  import RecipeTagInput from "./recipe-tag-input.vue";
  import RecipeActionsButton from "./recipe-actions-button.vue";
  import RecipePagesButton from "./recipe-pages-button.vue";
  import RecipeNameCell from "./recipe-name-cell.vue";
  import TagLabel from "./tag-label.vue";

  import { parseISO, formatRelative } from "date-fns";
  import { sortBy } from "lodash-es";
  import { storeToRefs } from "pinia";
  import { onBeforeMount } from "vue";

  import { useSelectionPrompt } from "./lib/use-bulk-actions.js";

  const showingSelectionPrompt = useSelectionPrompt("recipe-bulk-selection:hint");

  const props = defineProps({
    recipeSearchStore: { type: Object, required: true },

    selectionMode: { type: Boolean, default: false },
    multipleSelectionMode: { type: Boolean, default: (props) => props.selectionMode },

    enableArchiving: { type: Boolean, default: true },
    allowLocking: { type: Boolean, default: false },
    deleteConfirms: { type: Object, default: null },

    allowInlineTagEdits: { type: Boolean, default: true },
    showTags: { type: Boolean, default: true },

    showRecipeActionsColumn: { type: Boolean, default: true },

    showModified: { type: Boolean, default: true },
    alwaysShowModifiedColumn: { type: Boolean, default: false },
  });

  onBeforeMount(() => {
    if (props.multipleSelectionMode && !props.selectionMode) {
      console.error(
        "Contradictory props: if multipleSelectionMode is true, selectionMode must be true"
      );
    }
    if (props.alwaysShowModifiedColumn && !props.showModified) {
      console.error(
        "Contradictory props: if alwaysShowModifiedColumn is true, showModified must be true"
      );
    }
  });

  const { visibleRecipes, selection, perPage, activeTags } = storeToRefs(props.recipeSearchStore);

  const expanded = _ref(new Array(perPage.value || 20).fill(false));

  function dateDisplay(updatedAt) {
    return formatRelative(parseISO(updatedAt), new Date());
  }

  function sortTags(tags) {
    return sortBy(tags, (t) => t.name.toLowerCase());
  }

  function shouldOpenInNewTab(recipe) {
    if (!props.multipleSelectionMode) return false;
    const count = selection.value.length;
    const isSelected = selection.value.includes(recipe.id);
    return count > 1 || (count === 1 && !isSelected);
  }

  async function handleSelectAll(isChecked) {
    const allIds = await props.recipeSearchStore.fetchSelection();
    if (isChecked) {
      props.recipeSearchStore.addToSelection(allIds);
    } else {
      props.recipeSearchStore.removeFromSelection(allIds);
    }
  }

  function handleSelectOne(isChecked, recipe) {
    if (!props.multipleSelectionMode) {
      props.recipeSearchStore.clearSelection();
    }
    if (isChecked) {
      props.recipeSearchStore.addToSelection([recipe.id]);
    } else {
      props.recipeSearchStore.removeFromSelection([recipe.id]);
    }
  }

  function handleClearSearch() {
    props.recipeSearchStore.resetSearch();
  }

  function handleTagClick(tag) {
    if (!activeTags.value.some((t) => t.name === tag.name)) {
      props.recipeSearchStore.setTagFilter([...activeTags.value, tag]);
    }
  }

  function handleSortClick(field) {
    props.recipeSearchStore.toggleSort(field);
  }
</script>

<style scoped lang="scss">
  @import "~jose_modern/css";

  $col-checkbox-width: 5em;
  $col-checkbox-width-mobile: 2em;

  .col-checkbox {
    width: $col-checkbox-width;

    & > label {
      min-height: 1em;
      display: flex;
      align-items: center;
      justify-content: center;

      padding: 0.75em;
      cursor: pointer;
    }
  }
  td.col-checkbox {
    padding: 0; // padding is on the label/checkbox rather than the cell
  }

  .col-modified {
    white-space: nowrap;
  }

  th.col-checkbox {
    text-align: center;
  }

  .tag-list-v {
    display: flex;
    gap: 0.5em;
    align-items: center;
    flex-wrap: wrap;
  }

  /* Hidden by default, shown only for mobile expansions */
  .updated-at-v {
    display: none;
  }

  .expand-button-v {
    padding-top: 2px;
  }

  .results-container.single {
    tr td.name a {
      color: $grey-darker !important;
    }

    tr.in-selection {
      td {
        color: $white;
        background-color: $dark !important;
        a {
          color: $white !important;
        }
      }
    }
  }

  table {
    tbody tr td {
      vertical-align: middle;
    }
  }

  @include desktop-only {
    tbody tr {
      border-top: 1px solid $grey-lighter;
    }
  }

  /// Tablet and smaller
  @include tablet-only {
    tbody tr {
      border-top: 1px solid $grey-lighter;
    }
  }

  @include until($widescreen) {
    table {
      & > thead > tr {
        display: flex;
        flex-direction: row;

        & > th:not(.col-checkbox) {
          flex-grow: 1;
        }
      }

      & > tbody {
        & > tr {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          align-items: center;

          & > td {
            border: none;
            flex-direction: row;
          }

          & > td.name {
            display: flex;
            flex-basis: 50%;
            flex-grow: 1;
            flex-direction: column;
          }

          & > td.col-actions {
            display: flex;
            order: 2;
          }

          & > td.col-tags {
            padding-top: 0;

            order: 3;
            flex-basis: 100%;
            display: flex;
            justify-content: space-between;

            .results-container.selection-mode & {
              margin-left: $col-checkbox-width;
            }
          }
        }
      }
    }
  }

  @include mobile {
    .col-checkbox {
      width: $col-checkbox-width-mobile;
    }

    .results-count-container {
      margin-bottom: 1rem;
    }

    table {
      margin-top: 0.5rem;

      & > thead {
        border: none;
      }

      & > th {
        border: none;
      }

      & > tbody {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        & > tr {
          border: 1px solid $grey-light;
          border-radius: $radius-large;
          background-color: transparent !important;

          & > td.col-checkbox {
            // padding is on the label/checkbox rather than the cell
            // (reason being: it makes for a bigger clickable area)
            padding: 0;

            & > label {
              padding: 0.5em 0 0.5em 0.75em;
            }
          }
        }

        & > tr:not(.is-expanded) {
          & > td.col-tags {
            display: none;
          }
        }

        & > tr.is-expanded {
          [data-expand-row] {
            .icon {
              transform: rotate(90deg);
            }
          }

          .updated-at-v {
            display: block;
          }

          & > td.col-tags {
            display: flex;

            .results-container.selection-mode & {
              margin-left: $col-checkbox-width-mobile;
            }
          }
        }
      }
    }
  }

  // narrow: drop actions to its own row so the chevron can pin right
  @media screen and (max-width: 395px) {
    table > tbody > tr > td.col-actions {
      flex-basis: 100%;
      justify-content: space-between;
    }

    .results-container.selection-mode table > tbody > tr > td.col-actions {
      margin-left: $col-checkbox-width-mobile;
    }
  }

  @include tablet {
    th.name {
      min-width: 50ch;
    }
  }

  @include until($tablet) {
    .table thead th {
      border: none;
      padding-top: 0;
    }
  }

  @include until($desktop) {
    th.col-modified {
      display: inline-flex;
      justify-content: flex-end;
    }
  }

  @keyframes checkbox-highlight-pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(50, 115, 220, 0.7);
      background-color: rgba(50, 115, 220, 0.1);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(50, 115, 220, 0.3);
      background-color: rgba(50, 115, 220, 0.2);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(50, 115, 220, 0);
      background-color: transparent;
    }
  }

  .highlight-checkbox-pulse {
    animation: checkbox-highlight-pulse 1s ease-out;
    border-radius: 4px;
    transition: background-color 0.2s ease;
  }
</style>
